import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) toast.error("This reset link is missing its token.");
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== confirm) return toast.error("Passwords do not match");
        setBusy(true);
        try {
            await axios.post(`${API_BASE}/auth/reset-password`, { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 2200);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-ocean-mesh grid place-items-center p-6">
            <div className="w-full max-w-md bg-white rounded-3xl border border-sand-200 shadow-card p-8">
                <Logo />
                {!token ? (
                    <div className="mt-6 text-center">
                        <AlertTriangle className="h-10 w-10 text-coral mx-auto" />
                        <h1 className="font-heading text-2xl text-ocean-700 mt-4">Invalid reset link</h1>
                        <p className="text-sm text-muted-foreground mt-2">Request a new link to continue.</p>
                        <Link to="/forgot" className="inline-block mt-4 text-sm text-secondary hover:underline">
                            Send a new reset link
                        </Link>
                    </div>
                ) : done ? (
                    <div className="mt-6 text-center">
                        <CheckCircle2 className="h-10 w-10 text-secondary mx-auto" />
                        <h1 className="font-heading text-2xl text-ocean-700 mt-4">Password updated</h1>
                        <p className="text-sm text-muted-foreground mt-2">Redirecting you to login...</p>
                    </div>
                ) : (
                    <>
                        <h1 className="font-heading text-2xl text-ocean-700 mt-6">Set a new password</h1>
                        <p className="text-sm text-muted-foreground mt-1">At least 8 characters.</p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label>New password</Label>
                                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" data-testid="reset-password-input" required minLength={8} autoFocus />
                            </div>
                            <div>
                                <Label>Confirm password</Label>
                                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm-input" required />
                            </div>
                            <Button type="submit" disabled={busy} className="w-full bg-ocean-700 text-white" data-testid="reset-submit-btn">
                                {busy ? "Saving..." : "Update password"}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
